/*
 * pure per-row decisions for a CSV preview: which rows need one, the side-by-side compare of a
 * flagged row with what it resembles, the progress line, the Apply label and the apply's
 * `decisions` list. Rows here are the raw UploadRun rows (goserver), not the view rows.
 */
import { EM_DASH, fmt_count, fmt_money, num } from '../../format';
import { with_ist } from '../../shared/ist';
import { ROW_CODE, VERDICT } from './params';

export const DECISION = { KEEP: 'KEEP', SKIP: 'SKIP' };

export const DECISION_LABEL = {
  [DECISION.KEEP]: 'Keep (different payment)',
  [DECISION.SKIP]: 'Skip (duplicate)'
};

// rows[].matches[].kind (goserver UploadMatch): an earlier row of this file, or an entry already saved.
export const MATCH_KIND = { FILE: 'FILE', SAVED: 'SAVED' };

const list = (v) => (Array.isArray(v) ? v : []);

export const is_flagged = (row) => !!row && row.verdict === VERDICT.NEEDS_CONFIRMATION;
// a row the apply would save unless skipped (counts.valid includes the flagged ones)
export const is_payable = (row) =>
  !!row && (row.verdict === VERDICT.VALID || row.verdict === VERDICT.NEEDS_CONFIRMATION);

// ---- decision state: { [line]: { action, reason } } ----

// Pressing the chosen button again clears it; a new action keeps the reason typed so far.
export const set_decision = (state, line, action) => {
  const cur = (state || {})[line];
  const next = { ...(state || {}) };
  if (cur && cur.action === action) delete next[line];
  else next[line] = { action, reason: cur ? cur.reason : '' };
  return next;
};

export const set_reason = (state, line, reason) => {
  const cur = (state || {})[line];
  if (!cur) return state || {};
  return { ...state, [line]: { ...cur, reason: String(reason || '') } };
};

export const decision_progress = (rows, state) => {
  const flagged = list(rows).filter(is_flagged);
  const decided = flagged.filter((r) => (state || {})[r.line]).length;
  return {
    decided,
    total: flagged.length,
    complete: decided === flagged.length,
    text: flagged.length
      ? `${fmt_count(decided)} of ${fmt_count(flagged.length)} flagged ${
          flagged.length === 1 ? 'row' : 'rows'
        } decided`
      : ''
  };
};

// N = payable rows not skipped; M = every other row (skipped by decision, or invalid).
export const apply_counts = (rows, state) => {
  const all = list(rows);
  const s = state || {};
  const apply_n = all.filter(
    (r) => is_payable(r) && !(s[r.line] && s[r.line].action === DECISION.SKIP)
  ).length;
  return { apply_n, skipped_n: all.length - apply_n };
};

export const apply_label = (rows, state) => {
  const { apply_n, skipped_n } = apply_counts(rows, state);
  return `Apply ${fmt_count(apply_n)} ${apply_n === 1 ? 'row' : 'rows'}${
    skipped_n ? ` (${fmt_count(skipped_n)} skipped)` : ''
  }`;
};

// Only decisions on payable rows are sent, in file order; a blank reason is left out.
export const decisions_payload = (rows, state) => {
  const s = state || {};
  return list(rows)
    .filter((r) => is_payable(r) && s[r.line])
    .map((r) => {
      const d = { line: r.line, action: s[r.line].action };
      const reason = String(s[r.line].reason || '').trim();
      if (reason) d.reason = reason;
      return d;
    });
};

// ---- what a flagged row resembles ----

const FILE_ROW_RE = /\brows? (\d+) of this file\b/gi;

// The server's `matches` (FILE rows first) when sent; else, for a run stored before matches existed,
// the saved lookalikes plus any "row N of this file" its messages name.
export const row_matches = (row) => {
  if (!row) return [];
  if (Array.isArray(row.matches) && row.matches.length) return row.matches;
  const saved = list(row.lookalikes).map((m) => ({ ...m, kind: MATCH_KIND.SAVED }));
  const lines = [];
  list(row.codes).forEach((c) => {
    String((c && c.message) || '').replace(FILE_ROW_RE, (_, n) => {
      if (lines.indexOf(Number(n)) === -1) lines.push(Number(n));
      return '';
    });
  });
  return [...lines.map((line) => ({ kind: MATCH_KIND.FILE, line })), ...saved];
};

const when_of_row = (r) =>
  r && r.entry_date ? `${r.entry_date}${r.entry_time ? ` ${r.entry_time}` : ''}` : '';
const money = (v) => (v === null || v === undefined || v === '' ? '' : fmt_money(num(v)));
const norm = (v) =>
  String(v === null || v === undefined ? '' : v)
    .trim()
    .toLowerCase();

const side_of_row = (r) => ({
  teacher: (r && r.teacher && (r.teacher.name || r.teacher.uuid)) || '',
  category: (r && (r.category_label || r.category)) || '',
  amount: money(r && r.amount),
  when: when_of_row(r),
  note: (r && r.note) || '',
  reference: (r && r.reference_id) || ''
});

// A match in the same six fields; a bare file-row match (line only) reads that row of the run.
const side_of_match = (m, rows_by_line) => {
  const bare = m.amount === null || m.amount === undefined || m.amount === '';
  if (m.kind === MATCH_KIND.FILE && bare && rows_by_line[m.line])
    return side_of_row(rows_by_line[m.line]);
  return {
    teacher: m.teacher_name || (m.teacher && m.teacher.name) || '',
    category: m.category_label || m.category || '',
    amount: money(m.amount),
    when: m.entry_at_ist || when_of_row(m),
    note: m.note || '',
    reference: m.reference_id || ''
  };
};

const SOURCE_TEXT = { MANUAL: 'Added by hand', CSV: 'CSV upload', SYSTEM: 'System' };

const added_by_name = (m) =>
  (m.added_by && (m.added_by.name || m.added_by.uuid)) || m.added_by_name || '';

const FIELDS = [
  ['teacher', 'Teacher'],
  ['category', 'Category'],
  ['amount', 'Amount'],
  ['when', 'Date and time'],
  ['note', 'Note'],
  ['reference', 'Reference']
];

// One compare per match: each field with both values and whether they differ (differences are
// what Ops judge "different payment" on; equal fields are greyed).
export const compare_view = (row, match, rows) => {
  const by_line = {};
  list(rows).forEach((r) => {
    by_line[r.line] = r;
  });
  const mine = side_of_row(row);
  const theirs = side_of_match(match, by_line);
  const saved = match.kind !== MATCH_KIND.FILE;
  return {
    key: saved
      ? `saved-${match.entry_uuid || match.uuid || match.reference_id || ''}`
      : `line-${match.line}`,
    title: saved ? 'Entry already saved' : `Row ${match.line} of this file`,
    fields: FIELDS.map(([key, label]) => ({
      key,
      label,
      mine: mine[key] || EM_DASH,
      theirs: theirs[key] || EM_DASH,
      differs: norm(mine[key]) !== norm(theirs[key])
    })),
    // a saved entry also says where it came from and who added it
    meta: saved
      ? [
          match.source ? `Source: ${SOURCE_TEXT[match.source] || match.source}` : '',
          added_by_name(match) ? `Created by ${added_by_name(match)}` : '',
          match.added_at_ist ? `on ${with_ist(match.added_at_ist)}` : ''
        ]
          .filter(Boolean)
          .join(' · ')
      : ''
  };
};

// A large-amount row: its amount against the confirmation threshold ('' when not flagged so).
export const large_amount_text = (row) => {
  const large = list(row && row.codes).some((c) => c && c.code === ROW_CODE.CONFIRM_REQUIRED);
  if (!large) return '';
  const t = row.large_threshold;
  const threshold = t === null || t === undefined || t === '' ? '' : fmt_money(num(t));
  return threshold
    ? `Amount ${money(row.amount)} is at or above the ${threshold} limit that needs a check.`
    : `Amount ${money(row.amount)} is large enough to need a check.`;
};

// ---- after the apply: the recorded decisions ----

// rows[].decision (server, once applied with decisions): { action, reason, by: { name, uuid }, at_ist }
export const decision_text = (d) => {
  if (!d || !d.action) return '';
  const who = (d.by && (d.by.name || d.by.uuid)) || 'someone';
  const verb = d.action === DECISION.SKIP ? 'Skipped' : 'Kept';
  return `${verb} by ${who}${d.at_ist ? ` on ${with_ist(d.at_ist)}` : ''}${
    d.reason ? `: ${d.reason}` : ''
  }`;
};

// { [line]: decision } for an applied run's rows ({} when none were decided).
export const recorded_decisions = (rows) =>
  list(rows).reduce((m, r) => {
    if (r && r.decision && r.decision.action) m[r.line] = r.decision;
    return m;
  }, {});

export const skipped_lines_text = (rows) =>
  list(rows)
    .filter((r) => r && r.decision && r.decision.action === DECISION.SKIP)
    .map((r) => `Row ${r.line}: ${decision_text(r.decision)}`);
